import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateEmail } from '@baex/shared';
import { isApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/Field';
import { focusFirstInvalid } from '../../lib/focusFirstInvalid';
import { useLogin } from './useAuth';

/**
 * The login screen (see the "Login screen" section): heading, email, password, one button,
 * nothing else on the page. Email + password is the specified mechanism — see the "Admin
 * Login" section for the decision and what it replaced.
 */

/** Never reveals whether the email, the password, or the account status was the problem. */
export const GENERIC_CREDENTIALS_ERROR = 'Email or password is incorrect.';
export const GENERIC_FAILURE_ERROR = 'Sign-in failed. Please try again.';
/** See the "Login screen" section: the per-address limiter has its own message, not the generic failure. */
export const RATE_LIMITED_ERROR = 'Too many sign-in attempts. Please wait a minute and try again.';

export function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  function checkEmail(value: string): string | undefined {
    const result = validateEmail(value);
    return result.ok ? undefined : result.reason;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setRequestId(null);

    const nextEmailError = checkEmail(email);
    const nextPasswordError = password.length === 0 ? 'Password is required.' : undefined;
    setEmailError(nextEmailError);
    setPasswordError(nextPasswordError);
    // The rule at the top of the "Admin Interface" section ends with "and focus moves to the first of them" —
    // which this screen, alone among the forms in the console, was not doing.
    if (
      focusFirstInvalid([
        { error: nextEmailError, ref: emailRef },
        { error: nextPasswordError, ref: passwordRef },
      ])
    )
      return;

    try {
      await login.mutateAsync({ email: email.trim().toLowerCase(), password });
      navigate('/transfers', { replace: true });
    } catch (error) {
      // See the "Login screen" section: whatever went wrong, the password field is cleared and the email
      // keeps what was typed — nobody should have to retype an address to try again.
      setPassword('');
      if (isApiError(error)) {
        if (error.status === 429) {
          setFormError(RATE_LIMITED_ERROR);
          return;
        }
        if (error.status === 401) {
          // Covers wrong email, wrong password and disabled account alike, and says
          // nothing that would let this form enumerate registered addresses.
          //
          // Deliberately without a request id. The "Login screen" section attaches one only to "the
          // request itself failed", and quoting one under "Email or password is
          // incorrect." made a mistyped password look like a fault in the service —
          // something to report rather than something to retype.
          setFormError(GENERIC_CREDENTIALS_ERROR);
          return;
        }
        setRequestId(error.requestId);
        setFormError(GENERIC_FAILURE_ERROR);
        return;
      }
      setFormError(GENERIC_FAILURE_ERROR);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <h1 className="mb-6 text-center text-[15px] font-semibold text-ink">
          BAEX Transfer File Service — Admin
        </h1>
        <form
          onSubmit={(event) => void onSubmit(event)}
          noValidate
          className="flex flex-col gap-4 rounded border border-hairline bg-surface p-5"
        >
          <TextField
            ref={emailRef}
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            value={email}
            error={emailError}
            onChange={(event) => setEmail(event.target.value)}
            onBlur={(event) => setEmailError(checkEmail(event.target.value))}
          />
          <TextField
            ref={passwordRef}
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            error={passwordError}
            onChange={(event) => setPassword(event.target.value)}
          />

          {formError ? (
            <div role="alert" className="rounded border border-danger bg-danger-soft px-3 py-2">
              <p className="text-[13px] text-ink">{formError}</p>
              {requestId ? (
                <p className="mt-0.5 text-2xs text-ink-muted">
                  request id <span className="tabular">{requestId}</span>
                </p>
              ) : null}
            </div>
          ) : null}

          <Button type="submit" variant="primary" disabled={login.isPending} className="w-full">
            {login.isPending ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </main>
  );
}
